import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link as LinkIcon } from "lucide-react";

function getSubmitLabel(submitting, isFirstLink) {
  if (submitting) return "Linking…";
  return isFirstLink ? "Link Account" : "Link this Client ID";
}

export default function LinkClientPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const alreadyLinked = (user?.client_ids || []).length;
  const isFirstLink = alreadyLinked === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/link-client", { client_code: code.trim().toUpperCase() });
      await refresh();
      toast.success(isFirstLink ? "Account linked successfully" : "Entity linked to your account");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-lg mx-auto">
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-8">
            <div className="w-10 h-10 bg-slate-900 flex items-center justify-center rounded-md mb-6">
              <LinkIcon className="w-5 h-5 text-white" />
            </div>
            <p className="label-tag mb-2">
              {isFirstLink ? "Account not linked" : "Add another entity"}
            </p>
            <h1 className="font-display text-3xl font-medium text-slate-900">
              {isFirstLink ? "Enter your Client ID" : "Link another Client ID"}
            </h1>
            <p className="text-sm text-slate-500 mt-2 mb-6">
              {isFirstLink
                ? "Your provider gave you a Client ID (e.g., "
                : "Enter the Client ID for another entity you'd like to see under this account (e.g., "}
              <span className="font-mono text-slate-700">CID-0001</span>){" "}
              to add it to your account.
            </p>

            {!isFirstLink && (user?.linked_clients || []).length > 0 && (
              <div className="mb-6 border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="already-linked-list">
                <p className="label-tag mb-2">Already linked</p>
                <div className="flex flex-wrap gap-1.5">
                  {(user?.linked_clients || []).map((lc) => (
                    <span
                      key={lc.id}
                      className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-white border border-slate-200 rounded-sm"
                    >
                      <span className="font-mono text-slate-500">{lc.client_code}</span>
                      <span className="text-slate-900">{lc.name}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="link-client-form">
              <div className="space-y-2">
                <Label className="label-tag">Client ID</Label>
                <Input
                  required
                  placeholder="CID-0001"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  data-testid="link-client-code-input"
                  className="rounded-md border-slate-300 font-mono tracking-wider text-lg h-12"
                />
              </div>
              <div className="flex gap-2">
                {!isFirstLink && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                    className="border-slate-300"
                    data-testid="link-client-back-btn"
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white h-11"
                  data-testid="link-client-submit-btn"
                >
                  {getSubmitLabel(submitting, isFirstLink)}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
